import React from 'react';
import styled from 'styled-components';
import { Pencil, Trash2 } from 'lucide-react';
import Modal from '../Modal';
import { AgentPayment, formatCurrency, formatDate } from '../../services/agentBalancesService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  payments: AgentPayment[];
  onEdit: (payment: AgentPayment) => void;
  onDelete: (payment: AgentPayment) => void;
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const AmountLine = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  direction: ltr;
  text-align: right;
`;

const MetaLine = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconBtn = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: ${p => (p.$danger ? 'var(--error-color)' : 'var(--text-secondary)')};
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
    color: ${p => (p.$danger ? 'var(--error-color)' : 'var(--primary-color)')};
  }
`;

const Empty = styled.p`
  text-align: center;
  color: var(--text-secondary);
  padding: 24px 0;
`;

const ClientPaymentsModal: React.FC<Props> = ({ isOpen, onClose, clientName, payments, onEdit, onDelete }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`תשלומים · ${clientName}`} width="500px">
      {payments.length === 0 ? (
        <Empty>לא נרשמו תשלומים ללקוח זה.</Empty>
      ) : (
        <List>
          {payments.map(p => (
            <Item key={p.id}>
              <Info>
                <AmountLine>{formatCurrency(p.amount)}</AmountLine>
                <MetaLine>
                  {formatDate(p.paymentDate)}
                  {p.reference ? ` · אסמכתא: ${p.reference}` : ''}
                  {p.note ? ` · ${p.note}` : ''}
                </MetaLine>
              </Info>
              <Actions>
                <IconBtn type="button" title="עריכה" onClick={() => onEdit(p)}><Pencil size={16} /></IconBtn>
                <IconBtn type="button" $danger title="מחיקה" onClick={() => onDelete(p)}><Trash2 size={16} /></IconBtn>
              </Actions>
            </Item>
          ))}
        </List>
      )}
    </Modal>
  );
};

export default ClientPaymentsModal;
